<template>
  <section
    class="my-8 md:mb-8 mx-auto bg-black pt-6 px-2 md:px-10 pb-0 shadow-black shadow-xl divide-y-2 divide-white font-helvetica text-[22px] text-[#999999] max-w-[1400px]"
  >
    <RallyLogoDownload />

    <!-- Hero / intro -->
    <div class="pt-8 mt-8 flex flex-col gap-4">
      <h1 class="text-white text-2xl md:text-4xl font-russo">
        Rally Route Planner — Plan Your Rally Route on the Map
      </h1>
      <h2 class="font-semibold text-white">
        Rally Route Mapping Software Built for Roadbook Creation
      </h2>
      <p class="text-[17px] md:text-[20px]">
        Rally Navigator is a rally route planner for organizers and competitors
        designing a new event. Plan your route on MapBox 3D terrain, switch to
        satellite imagery to scout the ground &mdash; then convert the planned route straight into a
        turn-by-turn navigation roadbook with tulips, distances and headings.
      </p>
      <picture class="contents">
        <source srcset="/images/create-cross-country/CC-Screen-Shot-1.webp" type="image/webp" />
        <img
          decoding="async"
          fetchpriority="high"
          width="1919"
          height="1029"
          src="/images/create-cross-country/CC-Screen-Shot-1.jpg"
          class="flex m-auto"
          alt="Rally route planned on a 3D terrain map in Rally Navigator"
        />
      </picture>
    </div>

    <!-- Map providers -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        Choose the Right Map Layer for the Terrain
      </h2>
      <p class="text-[17px] md:text-[20px]">
        Every rally is different, so Rally Navigator gives you several map
        layers to plan and navigate with:
      </p>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-3 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li>
          <span class="text-white">MapBox 3D terrain maps</span> &mdash; read
          the relief of mountains, dunes and valleys before you ever leave home.
        </li>
        <li>
          <span class="text-white">Satellite &amp; aerial imagery</span> &mdash;
          scout tracks, junctions and hazards on real imagery of the ground.
        </li>
      </ul>
      <picture class="contents">
        <source srcset="/images/toolbar3adad-1024x576.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="1024"
          height="576"
          src="/images/toolbar3adad-1024x576.png"
          class="flex m-auto"
          alt="Map layers and route planning tools in Rally Navigator"
        />
      </picture>
    </div>

    <!-- Draw the route -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        Draw Your Rally Route on the Map
      </h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-3 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li>
          Click along the map to lay down your route, or use snap-to-roads to
          trace existing roads automatically.
        </li>
        <li>
          Import an existing GPX or KML track to drop a recorded route straight
          onto the map.
        </li>
        <li>
          Fine-tune the line with track handles so distances and headings are
          measured accurately.
        </li>
        <li>
          Overlay a pre-run GPX track to check and correct the route on the map
          before the event.
        </li>
      </ul>
      <picture class="contents">
        <source srcset="/images/create-road-stage/RR-Screenshot-2.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="1919"
          height="1029"
          src="/images/create-road-stage/RR-Screenshot-2.png"
          class="flex m-auto"
          alt="Drawing and importing a rally route on the map in Rally Navigator"
        />
      </picture>
    </div>

    <!-- Map to roadbook -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        From Planned Route to Roadbook
      </h2>
      <p class="text-[17px] md:text-[20px]">
        A map is only the start. Once your route is on the map, Rally Navigator
        builds a complete
        <router-link
          to="/rally-roadbook"
          class="text-[#FF0000] hover:underline"
          >rally roadbook</router-link
        >
        from it &mdash; with tulip diagrams, FIA navigation icons and a
        print-ready PDF. See how it works for
        <router-link
          to="/how-to-create-a-cross-country"
          class="text-[#FF0000] hover:underline"
          >cross-country</router-link
        >
        and
        <router-link
          to="/how-to-create-a-road-rally"
          class="text-[#FF0000] hover:underline"
          >road &amp; stage rallies</router-link
        >.
      </p>
      <router-link to="/download" class="m-auto">
        <h2
          class="text-[#FF0000] font-russo text-[22px] md:text-[28px] hover:underline text-center"
        >
          Download Rally Navigator and Plan Your Rally Route Free
        </h2>
      </router-link>
    </div>
  </section>

  <section class="my-8 mx-auto max-w-[1400px]">
    <FeaturesBanner />
    <PartnersSmall />
    <RallyLogoDownload link-text="Download Rally Navigator" />
  </section>
  <RelatedGuides
    :links="[
        { to: '/how-to-create-a-cross-country', text: 'How to Create a Cross Country Roadbook' },
        { to: '/how-to-create-a-road-rally', text: 'How to Create a Road Rally Roadbook' },
        { to: '/rally-tulip-diagrams', text: 'Rally Tulip Diagrams' },
        { to: '/rally-roadbook', text: 'What Is a Rally Roadbook?' },
      ]"
  />
</template>

<script>
import FeaturesBanner from './FeaturesBanner.vue';
import RelatedGuides from './RelatedGuides.vue';
import PartnersSmall from './PartnersSmall.vue';
import RallyLogoDownload from './RallyLogoDownload.vue';

export default {
  name: 'RallyMaps',
  components: {
    FeaturesBanner,
    RelatedGuides,
    PartnersSmall,
    RallyLogoDownload,
  },
};
</script>

<style scoped></style>
